import { useRef, useState } from 'react';
import { Button, Divider, IconButton, Stack, TextField, Tooltip, Typography, useTheme } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from 'hooks/useAuthContext';
import { useCreateParticipantsLight } from 'hooks/useCreateParticipantsLight';
import { UploadButton } from '../EmailsInput/EmailsInput.styles';
import { TabDialogActions, TabDialogContent } from '../InviteUserDialog/InviteUserDialog.styles';
import { CountTextField, HiddenFileInput, ParticipantRowContainer, ParticipantRowStack, ParticipantsListBox, RowActionBox, RowErrorTypography, RowIndexTypography } from './CreateParticipantsLightTab.styles';

export const MAX_PARTICIPANTS_LIGHT = 30;

const EMPTY_PARTICIPANT = { name: '', tagA: '', tagB: '' };

type ParticipantLightData = { name: string; tagA: string; tagB: string };
type ParticipantLightFormValues = {
  count: string;
  participants: ParticipantLightData[];
};

const hasAnyValue = (participant: ParticipantLightData) =>
  [participant.name, participant.tagA, participant.tagB].some((value) => value.trim() !== '');

const toNullableValue = (value: string): string | null => {
  const trimmedValue = value.trim();
  return trimmedValue === '' ? null : trimmedValue;
};

const createParticipantsLightSchema = (t: (key: string, options?: Record<string, unknown>) => string) => z.object({
  count: z.string().refine((value) => {
    const parsed = Number(value);
    return value.trim() !== '' && Number.isInteger(parsed) && parsed >= 1 && parsed <= MAX_PARTICIPANTS_LIGHT;
  }, { message: t('createParticipantsLight.countRange', { max: MAX_PARTICIPANTS_LIGHT }) }),
  participants: z
    .array(
      z.object({
        name: z.string(),
        tagA: z.string(),
        tagB: z.string()
      }).refine(hasAnyValue, {
        message: t('createParticipantsLight.atLeastOneValue'),
        path: ['name']
      })
    )
    .min(1, { message: t('createParticipantsLight.noUsers') })
    .max(MAX_PARTICIPANTS_LIGHT, { message: t('createParticipantsLight.countRange', { max: MAX_PARTICIPANTS_LIGHT }) })
});

interface CreateParticipantsLightTabProps {
  onClose: () => void;
}

export const CreateParticipantsLightTab: React.FC<CreateParticipantsLightTabProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { userState } = useAuthContext();
  const { createParticipantsLight, creatingParticipantsLight } = useCreateParticipantsLight();
  const [csvError, setCsvError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { control, handleSubmit, setValue, trigger, clearErrors, formState: { errors, isValid } } = useForm<ParticipantLightFormValues>({
    resolver: zodResolver(createParticipantsLightSchema(t)),
    mode: 'onChange',
    defaultValues: {
      count: '',
      participants: []
    }
  });

  const { fields, append, remove, replace } = useFieldArray({ control, name: 'participants' });

  const syncParticipantsCount = (requestedCount: number) => {
    const currentCount = fields.length;

    if (requestedCount > currentCount) {
      append(
        Array.from({ length: requestedCount - currentCount }, () => ({ ...EMPTY_PARTICIPANT })),
        { shouldFocus: false }
      );
      return;
    }

    if (requestedCount < currentCount) {
      remove(Array.from({ length: currentCount - requestedCount }, (_, index) => currentCount - 1 - index));
    }
  };

  const handleCountChange = (rawValue: string, onChange: (value: string) => void) => {
    const sanitizedValue = rawValue.replace(/\D/g, '');
    onChange(sanitizedValue);
    setCsvError('');

    if (sanitizedValue === '') {
      clearErrors('participants');
      replace([]);
      return;
    }

    const parsedValue = Number(sanitizedValue);
    if (!Number.isInteger(parsedValue) || parsedValue < 0 || parsedValue > MAX_PARTICIPANTS_LIGHT) {
      return;
    }

    syncParticipantsCount(parsedValue);
  };

  const handleRemoveParticipant = (index: number) => {
    remove(index);
    setValue('count', String(fields.length - 1), { shouldValidate: true });
  };

  const handleUploadButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = (loadEvent) => {
      const contents = (loadEvent.target?.result as string) ?? '';
      const names = contents
        .split(/\r?\n/)
        .flatMap((line) => line.split(','))
        .map((name) => name.trim())
        .filter((name) => name !== '');

      if (names.length === 0) {
        setCsvError(t('createParticipantsLight.csvEmpty'));
        return;
      }

      const limitedNames = names.slice(0, MAX_PARTICIPANTS_LIGHT);
      setCsvError(names.length > MAX_PARTICIPANTS_LIGHT
        ? t('createParticipantsLight.csvTooManyNames', { max: MAX_PARTICIPANTS_LIGHT })
        : '');

      clearErrors('participants');
      replace(limitedNames.map((name) => ({ ...EMPTY_PARTICIPANT, name })));
      setValue('count', String(limitedNames.length), { shouldValidate: true });
      trigger('participants');
    };
    reader.readAsText(file);
  };

  const onSubmit = async (values: ParticipantLightFormValues) => {
    try {
      await createParticipantsLight({
        participants: values.participants.map((participant) => ({
          name: toNullableValue(participant.name) ?? t('createParticipantsLight.defaultName'),
          tagA: toNullableValue(participant.tagA),
          tagB: toNullableValue(participant.tagB)
        }))
      });
      onClose();
    } catch {
      // błąd jest obsługiwany przez snackbar w useApiCall
    }
  };

  return (
    <>
      <TabDialogContent>
          <Stack
            direction={'column'}
            paddingLeft={theme.spacing(10)}
            paddingRight={theme.spacing(10)}
            paddingBottom={theme.spacing(5)}
            spacing={theme.spacing(3)}
          >
            <Stack>
              <Typography variant='h5' fontWeight={'600'}>
                {t('createParticipantsLight.title')}
              </Typography>
              <Typography variant='subtitle1' color={theme.palette.text.secondary}>
                {t('createParticipantsLight.subtitle', { organizationName: userState.user.selectedWorkspace.workspaceName })}
              </Typography>
            </Stack>

            <Typography variant='subtitle2'>{t('createParticipantsLight.description')}</Typography>
            <Controller
              name='count'
              control={control}
              render={({ field }) => (
                <CountTextField
                  data-testid="participants-light-count-input"
                  variant='outlined'
                  label={t('createParticipantsLight.usersCount')}
                  value={field.value}
                  onChange={(event) => handleCountChange(event.target.value, field.onChange)}
                  onBlur={field.onBlur}
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                  error={!!errors.count}
                  helperText={errors.count?.message ?? t('createParticipantsLight.maxUsersHint', { max: MAX_PARTICIPANTS_LIGHT })}
                />
              )}
            />
            <Divider />

            <Stack
              flexDirection={'column'}
              display={'flex'}
              alignItems={'flex-start'}
              paddingTop={theme.spacing(2)}
              spacing={theme.spacing(2)}
            >
              <Typography variant='subtitle2'>{t('createParticipantsLight.orUploadFile')}</Typography>
              <HiddenFileInput
                accept=".csv,.txt"
                id="participants-light-file-input"
                data-testid="participants-light-file-input"
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
              />
              <UploadButton variant="outlined" onClick={handleUploadButtonClick}>
                {t('createParticipantsLight.uploadFileButton')}
              </UploadButton>
              {csvError && <Typography variant="caption" color="error">{csvError}</Typography>}
            </Stack>

            <Stack spacing={theme.spacing(2)}>
              <Divider />
              {fields.length === 0
                ? <Typography variant='body2' color={theme.palette.text.secondary}>{t('createParticipantsLight.emptyList')}</Typography>
                : (
                <ParticipantsListBox data-testid="participants-light-list">
                  {fields.map((field, index) => {
                    const rowErrorMessage = errors.participants?.[index]?.name?.message;

                    return (
                    <ParticipantRowContainer key={field.id}>
                    <ParticipantRowStack>
                      <RowIndexTypography variant='body2'>
                        {index + 1}.
                      </RowIndexTypography>
                      <Controller
                        name={`participants.${index}.name`}
                        control={control}
                        render={({ field: nameField }) => (
                          <TextField
                            {...nameField}
                            data-testid={`participant-light-name-${index}`}
                            label={t('createParticipantsLight.name')}
                            fullWidth
                            error={!!rowErrorMessage}
                          />
                        )}
                      />
                      <Controller
                        name={`participants.${index}.tagA`}
                        control={control}
                        render={({ field: tagAField }) => (
                          <TextField
                            {...tagAField}
                            onChange={(event) => {
                              tagAField.onChange(event);
                              trigger(`participants.${index}.name`);
                            }}
                            data-testid={`participant-light-tagA-${index}`}
                            label={t('createParticipantsLight.tagA')}
                            fullWidth
                            error={!!rowErrorMessage}
                          />
                        )}
                      />
                      <Controller
                        name={`participants.${index}.tagB`}
                        control={control}
                        render={({ field: tagBField }) => (
                          <TextField
                            {...tagBField}
                            onChange={(event) => {
                              tagBField.onChange(event);
                              trigger(`participants.${index}.name`);
                            }}
                            data-testid={`participant-light-tagB-${index}`}
                            label={t('createParticipantsLight.tagB')}
                            fullWidth
                            error={!!rowErrorMessage}
                          />
                        )}
                      />
                      <RowActionBox>
                        <Tooltip title={t('createParticipantsLight.removeUser')}>
                          <IconButton
                            data-testid={`participant-light-remove-${index}`}
                            onClick={() => handleRemoveParticipant(index)}
                            aria-label={t('createParticipantsLight.removeUser')}
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </Tooltip>
                      </RowActionBox>
                    </ParticipantRowStack>
                    {rowErrorMessage && (
                      <RowErrorTypography variant='caption' data-testid={`participant-light-error-${index}`}>
                        {rowErrorMessage}
                      </RowErrorTypography>
                    )}
                    </ParticipantRowContainer>
                    );
                  })}
                </ParticipantsListBox>
                )}
            </Stack>
          </Stack>
      </TabDialogContent>
      <TabDialogActions>
        <Stack paddingLeft={theme.spacing(10)} paddingRight={theme.spacing(10)} direction='row' spacing={theme.spacing(3)}>
          <Button onClick={onClose} variant='outlined'>{t('general.cancel')}</Button>
          <LoadingButton
            data-testid="create-participants-light-button"
            loading={creatingParticipantsLight}
            disabled={!isValid || fields.length === 0}
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            color="primary">
            {t('createParticipantsLight.createButton')}
          </LoadingButton>
        </Stack>
      </TabDialogActions>
    </>
  );
};
